import { isRunActive, subscribeRunLock } from '../harness/runLock';
import { EnvironmentError } from './errors';
import { log } from './logger';

/**
 * The service-worker update prompt.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * `vite.config.ts` sets `registerType: 'prompt'`, which is correct — OBS-4 forbids a force-reload
 * during a live state, and `autoUpdate` does exactly that. But choosing `prompt` and then not
 * providing one means the new service worker downloads, enters `waiting`, and stays there for
 * ever: **an installed instance serves its original bundle permanently.**
 *
 * That shipped. A verified fix was deployed and Andrew's phone went on running the old bundle, and
 * the only way anyone could tell was by comparing behavior by hand. The sharper consequence is that
 * every future harness fix is invisible on the device unless the operator knows to force-quit — so
 * a measurement can be taken with an instrument you believe is current and is not, which is a
 * silent failure at a real match.
 *
 * ── Why it is gated on the run lock ─────────────────────────────────────────
 * Applying an update reloads the page. Mid-soak or mid-live-tap that destroys the run outright.
 * OBS-4's "never force-reload during an in-match state" is the product's rule; the same hazard
 * applies here and is worse, because the thing being destroyed is the measurement itself. So the
 * prompt is **withheld** while a run holds the lock and appears the moment it releases — which is
 * what `subscribeRunLock` is for. `applyUpdate` re-checks as well: a stale render must not be able
 * to reload a run out from under itself.
 */

const MODULE = 'swUpdate';

/** What `virtual:pwa-register` hands back. Narrowed to what is actually called. */
export type ApplyUpdateFn = (reloadPage?: boolean) => Promise<void>;

let pendingApply: ApplyUpdateFn | null = null;
const listeners = new Set<() => void>();
let unsubscribeRunLock: (() => void) | null = null;

function notify(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch (cause) {
      log.exception(MODULE, 'an update listener threw', cause);
    }
  }
}

/**
 * Subscribes to changes in whether the prompt should be shown.
 *
 * Fires when an update becomes available **and** when the run lock changes, because "should the
 * prompt be visible" depends on both. Shaped for `useSyncExternalStore`.
 */
export function subscribeSwUpdate(listener: () => void): () => void {
  listeners.add(listener);

  // Bridged lazily and only once: nothing should hold a lock subscription in a build that never
  // renders the banner.
  unsubscribeRunLock ??= subscribeRunLock(notify);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      unsubscribeRunLock?.();
      unsubscribeRunLock = null;
    }
  };
}

/** Called from the `onNeedRefresh` callback: a new bundle is downloaded and waiting. */
export function notifyUpdateReady(apply: ApplyUpdateFn): void {
  pendingApply = apply;
  log.info(MODULE, 'a new bundle is waiting', { heldByRun: isRunActive() });
  notify();
}

/** True once a new bundle is waiting, regardless of whether it can be applied yet. */
export function isUpdateReady(): boolean {
  return pendingApply !== null;
}

/** True when an update is waiting but a run is holding it back. Surfaced so the wait is explained. */
export function isUpdateHeldByRun(): boolean {
  return pendingApply !== null && isRunActive();
}

/** The single predicate the banner renders on. */
export function shouldPromptUpdate(): boolean {
  return pendingApply !== null && !isRunActive();
}

/**
 * Applies the waiting update. This posts `SKIP_WAITING` and reloads the page.
 *
 * @throws {EnvironmentError} if a run is active — defence in depth behind the hidden banner — or if
 *   nothing is waiting. Never reloads silently out from under a measurement.
 */
export async function applyUpdate(): Promise<void> {
  if (pendingApply === null) {
    throw new EnvironmentError('There is no waiting update to apply.');
  }
  if (isRunActive()) {
    throw new EnvironmentError(
      'A run is in progress. Applying an update reloads the page and would destroy it — finish or discard the run first.',
    );
  }

  const apply = pendingApply;
  pendingApply = null;
  log.info(MODULE, 'applying the waiting update; the page will reload');
  notify();
  await apply(true);
}

/** Test seam. */
export function __resetSwUpdateForTests(): void {
  pendingApply = null;
  listeners.clear();
  unsubscribeRunLock?.();
  unsubscribeRunLock = null;
}
